import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import { useHistory } from 'react-router-dom';
import useCostCenterService from '../../app/services/hooks/useCostCenterService';
import { CostCenterDataForm, CostCenterFilters } from '../../app/models/CostCenter';
import CostCenterDataTable from './components/tables/CostCenterDataTable';
import CostCenterCreate from './components/CostCenterCreate';
import CostCenterEdit from './components/CostCenterEdit';
import useNavigationPage from '../../hooks/useNavigationPage';


const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'centros de costos',
        url: '/cost-centers',
        isActive: true
    }
];

const CostCenters = () => {
    const { navigationPage } = useNavigationPage();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const { fetchingGetCostCenters, getCostCenters, deleteCostCenter, activeCostCenter } =
        useCostCenterService();
    const [costCenters, setCostCenters] = useState<CostCenterDataForm[]>([]);

    const [costCenterIdEdit, setCostCenterIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [costCenterFilter, setCostCenterFilter] = useState<CostCenterFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [costCenterFilter]);

    const getAllCostCenter = () => {
        getCostCenters(costCenterFilter, {
            onSuccess: (response: ServiceResponse) => {
                setCostCenters(response.data.cost_centers);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (costCenterId: number) => {
        setShowingEdit(true);
        setCostCenterIdEdit(costCenterId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setCostCenterIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllCostCenter();
    };

    const destroy = (costCenterId: number) => {
        const _text = 'Está a punto de eliminar el centro de costo #' + costCenterId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando centro de costo...');
                        deleteCostCenter(costCenterId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'EL centro de costo no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };


    return (
        <>
            <Breadcrumbs pageSection="Centros de costos" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo centro de costo" />}
                />
                <CostCenterDataTable
                    costCenters={costCenters}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetCostCenters}
                    filter={costCenterFilter}
                    setFilter={setCostCenterFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear centro de costo"
                    backdrop={true}
                    showFooter={false}
                >
                    <CostCenterCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar centro de costo"
                    backdrop={true}
                    showFooter={false}
                >
                    <CostCenterEdit
                        costCenterId={costCenterIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default CostCenters;
